import React, { useState, useMemo, useEffect } from 'react';
import { useFormikContext, FieldArray } from 'formik';
import { Grid } from '@material-ui/core';
import { isEqual } from 'lodash-es';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { paymentMethod, policyTypeNames, policyType, standaloneRentersStates } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { ABODE_PRO_PLAN_AFFINITY, ABODE_PRO_PLAN_PROVIDER_NAME, tooltipHoverTexts } from 'core/helpers/constants';
import { FormField } from 'core/components/form';
import ValueField from 'core/components/value-field';
import { LabelTooltip } from 'core/components/label-tooltip';
import RemoveButton from 'core/components/remove-button';
import AddButton from 'core/components/add-button';
import { Card } from 'core/components/card';
import useFormattedAddress from 'core/hooks/use-formatted-address';
import useSession from 'core/hooks/use-session';
import { isMicroBiltBypassAllowed } from 'core/helpers/microbilt-bypass';
import { isCollectPaymentLaterAllowed } from 'core/helpers/collect-payment-later';
import { useStore } from 'core/store';
import { ACHFields } from 'common/components/payment/ach-fields';
import RestoreCheckoutDataDialog from 'offer/components/dialog/restore-checkout-data';
import { getPaymentMethods, isFirstInstallmentCC, isFirstInstallmentACH } from 'offer/components/checkout/helper';
import useCheckoutContext from '../../../hooks/use-checkout-context';
import { CreditCardField } from '../credit-card-field';
import { Attestation } from '../../attestation';
import { Label } from 'core/components/label';
import Section from 'core/components/section';
import infoGreenIcon from 'core/assets/svg/info-icon-dark.svg';
import useStyles from './billing-form.styles';

const BillingForm = observer(function BillingForm() {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, errors } = useFormikContext();
  const { canBindDirectly } = useCheckoutContext();
  const { canApproveUWReview } = useSession();
  const {
    offer: { offer, checkoutFormData, saveCheckoutFormData, state }
  } = useStore();
  const { autoPaymentMethod, condoPaymentMethod, homeownersPaymentMethod, rentersPaymentMethod, farmPaymentMethod } =
    values;
  const { option: selectedOption } = useParams();
  const address2 = values.address?.address2?.trim() === 'Apt' ? '' : values.address.address2?.trim() || '';
  const disableNoAptCheckbox = address2 && address2?.length > 0;

  const [openRestoreDataModal, setOpenRestoreDataModal] = useState(false);

  const { hasCreditCard, hasEscrow, hasACH } = useMemo(
    () =>
      getPaymentMethods({
        paymentMethods: values,
        policyType: selectedOption,
        noBindHome: offer.quote.noBindHome,
        noBindAuto: offer.quote.noBindAuto,
        hasAbodeProPlan:
          offer?.quote?.global?.affinity === ABODE_PRO_PLAN_AFFINITY &&
          offer?.quote?.connectedHome?.providerName === ABODE_PRO_PLAN_PROVIDER_NAME
      }),
    [values, offer, selectedOption]
  );

  // "Collect payment information later" (#33891) is a bind-level flag, so the checkbox renders once
  // here (not per payment-method card — a mismatched bundle renders two cards and would duplicate the
  // DOM id). Underwriter-only + brand-gated (the backend re-checks both). Only relevant when a Stripe
  // method (CC/ACH) is being collected, and never alongside a sweep-account bind, which still needs
  // the member's own method saved for installments (the backend hard-rejects that combination).
  const showCollectPaymentLater =
    canBindDirectly &&
    canApproveUWReview &&
    isCollectPaymentLaterAllowed() &&
    !values.useSweepAccount &&
    (hasCreditCard || hasACH);

  const checkPayments = () => {
    if (!hasCreditCard && checkoutFormData?.creditCardInfo) {
      setFieldValue('recoveredPaymentData', null);
      saveCheckoutFormData({ id: values.address, creditCardInfo: null });
    }
  };

  useEffect(() => {
    checkPayments();
    const canRestoreCheckoutData =
      !!checkoutFormData?.id &&
      isEqual(checkoutFormData.id, values.address) &&
      !!(checkoutFormData.phone || checkoutFormData.email);
    const canRestorePaymentMethod =
      (checkoutFormData?.ach && hasACH) || !!(checkoutFormData?.creditCardInfo && hasCreditCard);
    if (canRestoreCheckoutData || canRestorePaymentMethod) {
      setOpenRestoreDataModal(true);
    }
  }, []);

  const mortgageAddress = useFormattedAddress(values?.mortgageDetails?.mortgageHolderAddress);
  const billToOtherAddress = useFormattedAddress(values?.billToOtherParty?.address);

  useEffect(() => {
    const { email, phone, address } = values;
    if (email || phone) {
      saveCheckoutFormData({ id: address, email, phone });
    }
  }, [values, saveCheckoutFormData]);

  const determineSectionTitle = (selectedOption) => {
    const policyTypes = policyTypeNames[selectedOption];
    return `${policyTypes} payment method`;
  };

  const displayPaymentMethod = (policyPaymentMethod, policyType, directBillPaymentMethod) => {
    const showCcInput =
      policyPaymentMethod === paymentMethod.CreditCard ||
      isFirstInstallmentCC({
        policyType,
        paymentMethod: policyPaymentMethod,
        firstInstallmentPaymentMethod: directBillPaymentMethod
      });
    const showAchInput =
      policyPaymentMethod === paymentMethod.ACH ||
      isFirstInstallmentACH({
        policyType,
        paymentMethod: policyPaymentMethod,
        firstInstallmentPaymentMethod: directBillPaymentMethod
      });
    const isBillToOther = policyPaymentMethod === paymentMethod.BillToOther;

    // For BillToOther, render the third-party info card first (read-only summary of the party
    // gathered on the Farm details page). The CC/ACH input below uses the same flow as Direct +
    // first-charge method — `isFirstInstallmentCC`/`...ACH` already cover BillToOther, so the
    // existing showCcInput/showAchInput branches handle it.
    const billToOtherInfoCard = isBillToOther ? (
      <Card type="primaryEditable" className={`${classes.paymentMethodInfo} ${classes.billToOtherInfoCard}`}>
        <Label type="greenSmall" className={classes.paymentMethodTitle}>
          Bill to other information
        </Label>
        <Grid container item xs={12}>
          <FormField type="value" name="billToOtherParty.name" label="Bill to other name" xs={6} />
          <FormField
            type="value"
            name="billToOtherParty.address"
            label="Bill to other address"
            xs={6}
            value={billToOtherAddress || '-'}
          />
        </Grid>
        <Grid item xs={12} className={classes.loanOfficerField}>
          <LabelTooltip
            mode="light"
            label="Bill to other email"
            tooltip={{
              label: 'More Info',
              onHoverText: values.billToOtherParty?.name
                ? `We'll send an email invoice to the entity provided to ensure that ${values?.billToOtherParty?.name} receives the bill for the one-time payment.`
                : 'Please add Bill To Other party details.'
            }}
          >
            <FormField type="value" mode="light" name="billToOtherParty.email" />
          </LabelTooltip>
        </Grid>
      </Card>
    ) : null;

    // "Collect payment information later" (#33891): when the bind-level checkbox (rendered once by
    // showPaymentMethodPerPolicyType) is checked, the policy issues with the selected payment
    // method/type (still used for rating) but no Stripe details are collected now — staff enter them
    // on the policy afterward — so we hide the CC/ACH input and show a note in its place. Gated on
    // showCollectPaymentLater so a stale value can't hide the input when the option isn't offered
    // (e.g. after a sweep bind is enabled).
    const collectPaymentLater = showCollectPaymentLater && Boolean(values.collectPaymentLater);
    const collectPaymentLaterNote = (
      <Grid container xs={12} className={classes.paymentInformation}>
        <img src={infoGreenIcon} alt="info" className={classes.infoIcon} />
        Payment information will be collected later. Enter the member&apos;s payment details on the policy in Staff
        after issuance.
      </Grid>
    );

    if (showCcInput) {
      return (
        <>
          {billToOtherInfoCard}
          <Card type="primaryEditable" className={classes.paymentMethodInfo}>
            <Label type="greenSmall" className={classes.paymentMethodTitle}>
              Credit card information
            </Label>
            {collectPaymentLater ? collectPaymentLaterNote : <CreditCardField />}
          </Card>
        </>
      );
    }
    if (showAchInput) {
      return (
        <>
          {billToOtherInfoCard}
          <Card type="primaryEditable" className={classes.paymentMethodInfo}>
            <Label type="greenSmall" className={classes.paymentMethodTitle}>
              Bank account information
            </Label>
            {collectPaymentLater ? (
              collectPaymentLaterNote
            ) : (
              <>
                <ACHFields />
                {canApproveUWReview && isMicroBiltBypassAllowed() && (
                  <FormField
                    mode="light"
                    type="checkbox"
                    name="bypassMicroBiltVerification"
                    id="bypassMicroBiltVerification"
                    label="Bypass MicroBilt verification (underwriting only)"
                    tooltipText="Skips MicroBilt verification for this bind. Use only for a known-good account MicroBilt can't verify (e.g. the agent's own account). This action is logged for audit."
                  />
                )}
              </>
            )}
          </Card>
        </>
      );
    }
    if (policyPaymentMethod === paymentMethod.Escrow) {
      return (
        <Card type="primaryEditable" className={classes.paymentMethodInfo}>
          <Label type="greenSmall" className={classes.paymentMethodTitle}>
            Mortgage lender information
          </Label>
          <Grid container item xs={12} className={classes.paymentBlock}>
            {/* MORTGAGE DETAILS READ ONLY VALUES */}
            <FormField type="value" name="mortgageDetails.mortgageHolderName" label="Lender Name" xs={6} />
            <FormField
              type="value"
              name="mortgageDetails.mortgageHolderAddress"
              label="Billing Address"
              xs={6}
              value={mortgageAddress || '-'}
            />
            {values.mortgageDetails.isCustom && (
              <>
                <Grid item xs={6}>
                  <ValueField
                    label="Email (optional)"
                    value={values.mortgageDetails.mortgageHolderEmail || 'Not provided'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ValueField
                    label="Fax (optional)"
                    value={values.mortgageDetails.mortgageHolderFax || 'Not provided'}
                  />
                </Grid>
              </>
            )}
            <FormField
              type="value"
              name="mortgageDetails.loanNumber"
              label="Loan Number"
              xs={4}
              value={values.mortgageDetails.loanNumber || '-'}
            />
            <Grid item xs={12} className={classes.loanOfficerField}>
              <LabelTooltip
                mode="light"
                label="Additional billing email (optional)"
                tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.loanOfficerEmail }}
              >
                <FormField mode="light" type="string" name="loanOfficerEmail" placeholder="agent@example.com" />
              </LabelTooltip>
            </Grid>
          </Grid>
        </Card>
      );
    }

    if (policyPaymentMethod === paymentMethod.BillToOther) {
      // No first-installment method chosen yet (legacy / pre-bind validation failure) — show only
      // the third-party info card so staff can see who will be billed once they pick CC/ACH.
      return billToOtherInfoCard;
    }

    if (policyPaymentMethod === paymentMethod.Direct) {
      return (
        <Card type="primaryEditable" className={classes.paymentMethodInfo}>
          <Label type="greenSmall" className={classes.paymentMethodTitle}>
            Direct invoice information
          </Label>
          <Grid container xs={12} className={classes.paymentInformation}>
            <img src={infoGreenIcon} alt="info" className={classes.infoIcon} />
            We will send an invoice to {values.email} to finalize payment for your policy.
          </Grid>
        </Card>
      );
    }
  };

  const showPaymentMethodPerPolicyType = () => {
    if (canBindDirectly) {
      if (selectedOption === policyType.Home || selectedOption === policyType.HomeBundle) {
        return (
          <Section title={determineSectionTitle(selectedOption)} type="SubSection">
            {displayPaymentMethod(homeownersPaymentMethod)}
          </Section>
        );
      }

      if (selectedOption === policyType.Auto || selectedOption === policyType.AutoBundle) {
        return (
          <Section title={determineSectionTitle(selectedOption)} type="SubSection">
            {displayPaymentMethod(autoPaymentMethod)}
          </Section>
        );
      }
      if (selectedOption === policyType.Renters) {
        return (
          <Section title={determineSectionTitle(selectedOption)} type="SubSection">
            {displayPaymentMethod(rentersPaymentMethod)}
          </Section>
        );
      }

      if (selectedOption === policyType.Condo) {
        return (
          <Section title={determineSectionTitle(selectedOption)} type="SubSection">
            {displayPaymentMethod(condoPaymentMethod)}
          </Section>
        );
      }

      if (isFarmBasedPolicyType(selectedOption)) {
        return (
          <Section title={determineSectionTitle(selectedOption)} type="SubSection">
            {displayPaymentMethod(farmPaymentMethod, policyType.Farm, values.directBillPreferredPaymentMethod)}
          </Section>
        );
      }

      if (selectedOption === policyType.ARBundle) {
        if (standaloneRentersStates[state] && rentersPaymentMethod !== autoPaymentMethod) {
          return (
            <>
              <Section title="Renters payment method" type="SubSection">
                {displayPaymentMethod(rentersPaymentMethod)}
              </Section>
              <Section title="Auto payment method" type="SubSection">
                {displayPaymentMethod(autoPaymentMethod)}
              </Section>
            </>
          );
        }
        return (
          <Section title={determineSectionTitle(selectedOption)} type="SubSection">
            {displayPaymentMethod(autoPaymentMethod)}
          </Section>
        );
      }

      if (selectedOption === policyType.HABundle) {
        if (homeownersPaymentMethod === autoPaymentMethod) {
          return (
            <Section title={determineSectionTitle(selectedOption)} type="SubSection">
              {displayPaymentMethod(homeownersPaymentMethod)}
            </Section>
          );
        }
        return (
          <>
            <Section title="Home payment method" type="SubSection">
              {displayPaymentMethod(homeownersPaymentMethod)}
            </Section>
            <Section title="Auto payment method" type="SubSection">
              {displayPaymentMethod(autoPaymentMethod)}
            </Section>
          </>
        );
      }

      if (selectedOption === policyType.CABundle) {
        if (condoPaymentMethod === autoPaymentMethod) {
          return (
            <Section title={determineSectionTitle(selectedOption)} type="SubSection">
              {displayPaymentMethod(condoPaymentMethod)}
            </Section>
          );
        }
        return (
          <>
            <Section title="Condo payment method" type="SubSection">
              {displayPaymentMethod(condoPaymentMethod)}
            </Section>
            <Section title="Auto payment method" type="SubSection">
              {displayPaymentMethod(autoPaymentMethod)}
            </Section>
          </>
        );
      }
    }
    if (!canBindDirectly && hasEscrow) {
      const title = () => {
        if (
          selectedOption === policyType.Home ||
          selectedOption === policyType.HomeBundle ||
          selectedOption === policyType.HABundle
        ) {
          return 'Home payment method';
        }
        if (selectedOption === policyType.Condo || selectedOption === policyType.CABundle) {
          return 'Condo payment method';
        }
      };

      return (
        <Section title={title()} type="SubSection">
          {displayPaymentMethod(paymentMethod.Escrow)}
        </Section>
      );
    }
  };

  return (
    <>
      <Card className={classes.container} type="primaryEditable">
        <Grid container item xs={12} spacing={4}>
          <FormField
            type="string"
            name="firstName"
            label="First Name"
            placeholder="Enter your First Name"
            mode="light"
            xs={6}
          />
          <FormField
            type="string"
            name="lastName"
            label="Last Name"
            placeholder="Enter your Last Name"
            mode="light"
            xs={6}
          />
        </Grid>
        <Grid container item xs={12} spacing={4}>
          <FormField
            type="address"
            name="address.address"
            placeholder="Enter your Mailing Address"
            label="Address"
            mode="light"
            xs={12}
          />
        </Grid>
        <Grid container xs={12} alignItems="center">
          <Grid item xs={6}>
            <LabelTooltip
              label="Apt, unit, floor, building, etc."
              tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.address2 }}
              mode="light"
            >
              <FormField
                type="string"
                name="address.address2"
                mode="light"
                disabled={values.noAptNumber}
                fast={false}
              />
            </LabelTooltip>
          </Grid>

          {(errors?.address?.address2 || values.noAptNumber) && (
            <Grid item xs={6}>
              <FormField
                type="checkbox"
                name="noAptNumber"
                mode="light"
                label="Address does not have apt/unit no., etc."
                onChange={(e) => {
                  if (e.target.checked) {
                    setFieldValue('address.address2', '');
                  }
                }}
                disabled={disableNoAptCheckbox}
              />
            </Grid>
          )}
        </Grid>

        <Grid container item xs={12} spacing={4}>
          <FormField type="string" name="address.city" label="City" mode="light" xs={4} />
          <FormField type="state" name="address.state" label="State" mode="light" xs={4} />
          <FormField type="string" name="address.zip" label="Zip" mode="light" xs={4} />
        </Grid>
        <Grid container item xs={12} spacing={4}>
          <FormField type="string" name="email" label="Email" placeholder="Email" mode="light" xs={6} />
          <FormField type="string" name="phone" label="Phone" placeholder="Phone Number" mode="light" xs={6} />
        </Grid>
        <FieldArray
          name="additionalPhoneNumbers"
          render={(arrayHelpers) => (
            <Grid container alignItems="center" spacing={2}>
              {values.additionalPhoneNumbers?.length !== 0 &&
                values.additionalPhoneNumbers?.map((_, index) => (
                  <Grid
                    container
                    key={`addtl-phone-${index}`}
                    className={classes.secondaryPhoneContainer}
                    alignItems="flex-end"
                    spacing={2}
                  >
                    <FormField
                      mode="light"
                      name={`additionalPhoneNumbers[${index}].phoneNumber`}
                      id={`additionalPhoneNumbers[${index}].phoneNumber`}
                      label="Secondary Phone"
                      type="string"
                      xs={4}
                    />
                    <FormField
                      mode="light"
                      name={`additionalPhoneNumbers[${index}].note`}
                      id={`additionalPhoneNumbers[${index}].note`}
                      label="Description"
                      type="string"
                      xs={7}
                    />
                    <RemoveButton
                      xs={2}
                      mode="big"
                      onClick={() => {
                        arrayHelpers.remove(index);
                        setFieldTouched('additionalPhoneNumbers');
                      }}
                    />
                    <FormField
                      mode="light"
                      name={`additionalPhoneNumbers[${index}].canText`}
                      id={`additionalPhoneNumbers[${index}].canText`}
                      label="Does the member agree to receiving texts at this number?"
                      type="checkbox"
                      className={classes.textCheckbox}
                    />
                  </Grid>
                ))}
              <AddButton
                type="full"
                mode="xl"
                onClick={() => {
                  arrayHelpers.push({ phoneNumber: '', note: '', canText: false });
                  setFieldTouched('additionalPhoneNumbers');
                }}
                label="Add an additional phone number"
              />
            </Grid>
          )}
        />
        <Grid className={classes.sectionWithLine}>
          <Attestation offer={offer} />
        </Grid>
      </Card>
      {showCollectPaymentLater && (
        <Card type="primaryEditable" className={classes.paymentMethodInfo}>
          <FormField
            mode="light"
            type="checkbox"
            name="collectPaymentLater"
            id="collectPaymentLater"
            label="Collect payment information later (underwriting only)"
            tooltipText="Issues the policy without collecting payment now. The selected payment method and type are still used for rating; enter the payment details on the policy in Staff after issuance. This action is logged for audit."
          />
        </Card>
      )}
      {showPaymentMethodPerPolicyType()}
      <RestoreCheckoutDataDialog
        open={openRestoreDataModal}
        hasCreditCard={hasCreditCard}
        onClose={() => setOpenRestoreDataModal(false)}
      />
    </>
  );
});

export default BillingForm;
